import { QueryHookOptions, gql } from '@apollo/client'
import { formatReactions } from 'app/features/group/helpers'
import { usePersistedCacheQuery } from 'app/hooks'
import { GroupPost, ReactionType } from 'app/types'
import { GroupAnnouncement } from 'app/types/GroupAnnouncement'
import { GroupEvent } from 'app/types/GroupEvent'
import { GroupVisibility } from 'app/types/GroupVisibility'

export const query = gql`
  query fetchMyGroupPosts($filter: myGroupPostsFilter!, $sort: String!, $page: Int!, $limit: Int!) {
    myGroupPosts(filter: $filter, sort: $sort, page: $page, limit: $limit) {
      totalDocs
      page
      docs {
        id
        text
        link
        totalComments
        createdAt
        editedAt
        type
        group {
          id
          name
          totalMembers
          visibility
          profileImage {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            slug
          }
          universityCourse {
            slug
          }
          allowedUserTypes
        }
        creator {
          id
          fullName
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            id
            name
          }
        }
        images {
          id
          image {
            id
            width
            height
            url
          }
        }
        reactions {
          id
          reaction
          user {
            id
          }
        }

        # Event
        event {
          id
          name
          description
          startDate
          endDate
          createdAt
          location
          image {
            id
            url
            width
            height
          }
        }

        # Announcement
        body
        scheduledAt
      }
    }
  }
`

export enum MyGroupPostsSort {
  DateAsc = 'createdAt',
  DateDesc = '-createdAt',
}

export interface MyGroupPostsQueryInput {
  filter: 'posts' | 'events' | 'announcements' | 'scheduled'
  sort: MyGroupPostsSort
  page: number
  limit: number
}

export interface MyGroupPostsQueryOutput {
  myGroupPosts: {
    page: number
    totalDocs: number
    docs: {
      type: 'post' | 'event' | 'announcement'
      id: string
      text: string
      link: string
      createdAt: string
      editedAt: string | null
      totalComments: number
      group: {
        id: string
        name: string
        visibility: GroupVisibility
        totalMembers: number
        profileImage: {
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university?: {
          slug: string
        }
        universityCourse?: {
          slug: string
        }
        allowedUserTypes: string[]
      }
      creator: {
        id: string
        fullName: string
        avatar: {
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university: {
          id: string
          name: string
        }
      }
      images: {
        image: {
          id: string
          width: number
          height: number
          url: string
        }
      }[]
      reactions: {
        id: string
        reaction: ReactionType
        user: {
          id: string
        }
      }[]
      event?: {
        id: string
        name: string
        description: string
        location?: string
        startDate: string
        endDate?: string
        createdAt: string
        image?: {
          id: string
          url: string
          width: number
          height: number
        }
      }
      body?: string
      scheduledAt?: string
    }[]
  }
}

const defaultVariables: MyGroupPostsQueryInput = {
  filter: 'posts',
  sort: MyGroupPostsSort.DateDesc,
  page: 1,
  limit: 10,
}

export const useMyGroupPostsQuery = (
  options: QueryHookOptions<MyGroupPostsQueryOutput, MyGroupPostsQueryInput>
) => usePersistedCacheQuery<MyGroupPostsQueryOutput, MyGroupPostsQueryInput>(query, options)

export const formatFn = (
  feedData?: MyGroupPostsQueryOutput['myGroupPosts']
): (GroupPost | GroupEvent | GroupAnnouncement)[] => {
  if (!feedData) return []
  const feed = feedData.docs || []
  return feed
    .map((feedItem) => {
      const group = {
        id: feedItem.group.id,
        name: feedItem.group.name,
        href: `/groups/${feedItem.group.id}`,
        profileImage: {
          small: feedItem.group.profileImage?.sizes.small.url || '',
        },
        university: feedItem.group.university,
        universityCourse: feedItem.group.universityCourse,
        allowedUserTypes: feedItem.group.allowedUserTypes || [],
      }

      const creator = {
        id: feedItem.creator.id,
        fullName: feedItem.creator.fullName,
        avatar: {
          medium: feedItem.creator.avatar?.sizes?.medium.url,
          small: feedItem.creator.avatar?.sizes?.small.url,
        },
      }

      if (feedItem.type === 'event' && feedItem.event) {
        const event = feedItem.event
        const formattedEvent: GroupEvent = {
          type: 'groupEvent',
          href: `/groups/events/${event.id}`,
          id: feedItem.id,
          eventId: event.id,
          name: event.name,
          description: event.description,
          location: event.location,
          startDate: event.startDate,
          endDate: event.endDate,
          createdAt: event.createdAt,
          creator: creator,
          group: group,
          ...(event.image && {
            image: {
              url: event.image.url,
              width: event.image.width,
              height: event.image.height,
            },
          }),
        }
        return formattedEvent
      }

      if (feedItem.type === 'post') {
        const reactions = formatReactions(feedItem.reactions)
        const formattedPost: GroupPost = {
          type: 'groupPost',
          href: `/groups/posts/${feedItem.id}`,
          id: feedItem.id,
          createdAt: feedItem.createdAt,
          editedAt: feedItem.editedAt,
          reactions,
          totalComments: feedItem.totalComments || 0,
          text: feedItem.text,
          creator: creator,
          group: group,
          ...(feedItem.images[0]?.image && {
            image: {
              url: feedItem.images[0].image.url,
              width: feedItem.images[0].image.width,
              height: feedItem.images[0].image.height,
            },
          }),
        }
        return formattedPost
      }

      if (feedItem.type === 'announcement') {
        const reactions = formatReactions(feedItem.reactions)
        const formattedAnnouncement: GroupAnnouncement = {
          type: 'groupAnnouncement',
          href: `/groups/posts/${feedItem.id}`,
          id: feedItem.id,
          createdAt: feedItem.createdAt,
          editedAt: feedItem.editedAt,
          totalComments: feedItem.totalComments || 0,
          title: feedItem.text,
          body: feedItem.body,
          scheduledDate: feedItem.scheduledAt,
          reactions,
          creator: creator,
          group: group,
        }
        return formattedAnnouncement
      }

      return null
    })
    .filter(Boolean) as (GroupPost | GroupEvent | GroupAnnouncement)[]
}

export const myGroupPostsQuery = {
  query,
  defaultVariables,
  formatFn,
}
